import { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { PersistentRoute } from './components/PersistentRoute';
import { LoadingState } from './components/StatePanel';
import { useAuth } from './context/AuthContext';
import { useCameraPreviewsReady } from './context/StreamManager';
import { DevicesPage } from './pages/DevicesPage';
import { EventsPage } from './pages/EventsPage';
import { LoginPage } from './pages/LoginPage';
import { ProfilePage } from './pages/ProfilePage';
import { ActivatePage } from './pages/ActivatePage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { SettingsPage } from './pages/SettingsPage';
import { ContourPage } from './pages/ContourPage';

const DeviceDetailsPage = lazy(() => import('./pages/DeviceDetailsPage').then((module) => ({ default: module.DeviceDetailsPage })));
const CameraSettingsPage = lazy(() => import('./pages/CameraSettingsPage').then((module) => ({ default: module.CameraSettingsPage })));
const CamerasPage = lazy(() => import('./pages/CamerasPage').then((module) => ({ default: module.CamerasPage })));
const ServersPage = lazy(() => import('./pages/FleetPages').then((module) => ({ default: module.ServersPage })));
const ComputersPage = lazy(() => import('./pages/FleetPages').then((module) => ({ default: module.ComputersPage })));
const TelemetryPage = lazy(() => import('./pages/TelemetryPage').then((module) => ({ default: module.TelemetryPage })));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((module) => ({ default: module.NotFoundPage })));

function ProtectedRoute() {
  const { isAuthenticated, isInitializing } = useAuth();
  const location = useLocation();
  if (isInitializing) return <LoadingState />;
  return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace state={{ from: location }} />;
}

function ShellRoute() {
  const { profile } = useAuth();
  return <PreparedShellRoute key={profile?.id} />;
}

function PreparedShellRoute() {
  const { pathname } = useLocation();
  const previewsReady = useCameraPreviewsReady();
  const [entered, setEntered] = useState(false);
  const camerasActive = pathname === '/cameras' || pathname === '/cameras/';

  useEffect(() => {
    if (entered) return;
    if (previewsReady) {
      setEntered(true);
      return;
    }
    const deadline = window.setTimeout(() => setEntered(true), 4_000);
    return () => window.clearTimeout(deadline);
  }, [entered, previewsReady]);

  if (!previewsReady && !entered) return <LoadingState label="Подготавливаем видео всех камер…" />;

  return (
    <AppShell>
      <PersistentRoute active={camerasActive}>
        <Suspense fallback={<LoadingState />}>
          <CamerasPage />
        </Suspense>
      </PersistentRoute>
      {!camerasActive && (
        <Suspense fallback={<LoadingState />}>
          <Outlet />
        </Suspense>
      )}
    </AppShell>
  );
}

function LandingRedirect() {
  const { isAuthenticated, isInitializing } = useAuth();
  if (isInitializing) return <LoadingState />;
  return <Navigate to={isAuthenticated ? '/cameras' : '/login'} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function ViewportManager() {
  useEffect(() => {
    const root = document.documentElement;
    const viewport = window.visualViewport;
    let focusTimer: number | undefined;

    const isFormControl = (element: Element | null): element is HTMLElement =>
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement ||
      element instanceof HTMLSelectElement ||
      (element instanceof HTMLElement && element.isContentEditable);

    const keepFocusedControlVisible = () => {
      if (!root.classList.contains('keyboard-open') || !isFormControl(document.activeElement)) return;
      window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => {
        (document.activeElement as HTMLElement).scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      }, 80);
    };

    const syncViewport = () => {
      const height = viewport?.height ?? window.innerHeight;
      const offsetTop = viewport?.offsetTop ?? 0;
      const keyboardInset = viewport && isFormControl(document.activeElement)
        ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
        : 0;

      root.style.setProperty('--visual-viewport-height', `${Math.round(height)}px`);
      root.style.setProperty('--visual-viewport-offset-top', `${Math.round(offsetTop)}px`);
      root.style.setProperty('--keyboard-inset', `${Math.round(keyboardInset)}px`);
      root.classList.toggle('keyboard-open', keyboardInset > 120);
      keepFocusedControlVisible();
    };

    const handleFocus = () => window.setTimeout(syncViewport, 40);
    const handleBlur = () => window.setTimeout(syncViewport, 80);

    syncViewport();
    viewport?.addEventListener('resize', syncViewport);
    viewport?.addEventListener('scroll', syncViewport);
    window.addEventListener('resize', syncViewport);
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);

    return () => {
      window.clearTimeout(focusTimer);
      viewport?.removeEventListener('resize', syncViewport);
      viewport?.removeEventListener('scroll', syncViewport);
      window.removeEventListener('resize', syncViewport);
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
      root.classList.remove('keyboard-open');
      root.style.removeProperty('--visual-viewport-height');
      root.style.removeProperty('--visual-viewport-offset-top');
      root.style.removeProperty('--keyboard-inset');
    };
  }, []);

  return null;
}

export default function App() {
  return (
    <>
      <ViewportManager />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<LandingRedirect />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/activate" element={<ActivatePage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<ShellRoute />}>
            <Route path="/dashboard" element={<Navigate to="/cameras" replace />} />
            <Route path="/devices" element={<DevicesPage />} />
            <Route path="/devices/cameras/:id" element={<CameraSettingsPage />} />
            <Route path="/devices/:id" element={<DeviceDetailsPage />} />
            <Route path="/cameras" element={null} />
            <Route path="/cameras/:id" element={<DeviceDetailsPage />} />
            <Route path="/servers" element={<ServersPage />} />
            <Route path="/computers" element={<ComputersPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/telemetry" element={<TelemetryPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/contour" element={<ContourPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
      </Routes>
    </>
  );
}
